// Number lifecycle — NumberLifecycleController (/v1/numbers/lifecycle).
//
// One place to manage a number: add → allot (any org) → push to the switch (create or
// update the row, choose the flow) → reassign → release / retire (park), plus the
// Number details drawer and its Settings tab. Master/platform admins and Green Giraffe
// (root org) admins only — the server answers 403 for anyone else, and the page uses
// that answer (GET /mode) to decide which view to show.
//
// Shapes are camelCase (Java records / maps), except `number` (a ProviderNumber entity,
// snake_case as everywhere else).
import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { responseFrom, errorFrom } from '../apiEnvelope';
import type { APIResponse } from 'types/ivr';
import type { ProviderNumber } from './providers.service';
import type {
  SwitchPushMode, SwitchPushPreview, SwitchPushApplyResult, SwitchPushRecord, SwitchRow, SwitchPushAction,
  SwitchPushPlan,
} from './switchPush.service';
import type { LockInTerm, NumberAllocation } from './numberOwnership.service';

export interface FlowDid { cliNumber: string; businessId?: string | null }

/** One flow on a switch (state_details), with its recording folder(s) and the DIDs routed to it. */
export interface FlowInfo {
  operatorId: string;
  serviceId: string;
  stateCount: number;
  recordingFolders: string[];
  languages: string[];
  dids: FlowDid[];
}

export interface AddResultRow { input: string; e164?: string | null; reason?: string; routingRow?: string; numberId?: number }

export interface AddResult {
  providerId: number;
  provider: string;
  dryRun: boolean;
  created: AddResultRow[];
  refused: AddResultRow[];
}

export interface AllotRequest {
  organizationId: string;
  term?: LockInTerm;
  autoRenew?: boolean;
  force?: boolean;
  /** Default ON: push to the switch in the same action. */
  push?: boolean;
  operatorId?: string | null;
  serviceId?: string | null;
  cliNumber?: string | null;
  previewHash?: string | null;
  note?: string | null;
}

export interface OwnershipPreview {
  currentHolder?: { id: string; name: string } | null;
  inLockIn: boolean;
  lockInEndsAt?: string | number | null;
  refusal?: string;
  term: LockInTerm;
  newTermEndsAt: string | number;
}

export interface AllotPreview {
  number: string;
  target: { id: string; name: string; businessId: string };
  ownership: OwnershipPreview;
  mode: SwitchPushMode;
  switch?: SwitchPushPreview;
  previewHash?: string;
}

/** What the switch did after an ownership change. */
export interface LifecyclePush {
  mode: SwitchPushMode;
  outcome: 'OK' | 'NO_CHANGE' | 'BLOCKED' | 'FAILED' | 'CONFLICT' | 'PREVIEW' | 'OFF';
  action?: SwitchPushAction | null;
  reason?: string | null;
  newLabel?: string | null;
  pushId?: number | null;
  cacheRefreshed?: boolean | null;
  warnings: string[];
  plan?: SwitchPushPlan | null;
}

export interface AllotResult { number: ProviderNumber; routingRow: string; push?: LifecyclePush }

export interface ReleaseResult { number: ProviderNumber; routingRow: string; push?: LifecyclePush }

export interface PushRequest { operatorId?: string | null; serviceId?: string | null; cliNumber?: string | null; previewHash?: string | null }

export interface BillingClaim { organizationId: string; organizationName: string; dataSource: string; pattern: string }

export interface SwitchView {
  switch: string;
  sourceKind?: string | null;
  home: boolean;
  rows?: SwitchRow[];
  note?: string;
  error?: string;
}

export interface NumberDetails {
  number: ProviderNumber;
  mode: SwitchPushMode;
  provider?: { id: number; name: string; terminatesOnOurSwitch: boolean } | null;
  holder?: { organizationId: string; name?: string | null; businessId?: string | null; since?: string | number } | null;
  tenures: NumberAllocation[];
  routingRow?: { cliNumber: string; businessId: string; operatorId: string; serviceId: string; active: boolean; providerId?: number } | null;
  switches: SwitchView[];
  billing: { label?: string | null; sourceKind?: string; claimedBy?: BillingClaim[]; holderChainClaims?: boolean; error?: string };
  pushes: SwitchPushRecord[];
  rollbackable?: number | null;
}

export interface PatternResult {
  label: string;
  organization: { id: string; name: string };
  sourceKind: string;
  changed: { dataSourceId: string; name: string; before: string; after: string }[];
  alreadyClaimed: boolean;
}

/** webhook_config as the browser may see it: never a token. */
export interface WebhookView {
  set: boolean;
  invalid?: boolean;
  url?: string | null;
  enabled?: boolean | string | null;
  authTokenSet?: boolean;
  secretSet?: boolean;
  timeoutMs?: string | null;
}

export interface SettingsValues {
  dialing_code?: string | null;
  transcribe_name?: string | null;
  default_language?: string | null;
  webhook_config?: WebhookView;
}

export interface CallerLookupSource {
  id: number; organizationId: string; didKey: string; vendor: string; vendorKey: string;
  enabled: boolean; hasWebhookToken: boolean; ringSeconds?: number;
}

export interface NumberSettings {
  numberId: number;
  e164: string;
  mode: SwitchPushMode;
  outboundAllowed: boolean;
  outboundCovers: string;
  outboundNotCovered: string;
  callerLookup?: { enabled?: boolean; sources?: CallerLookupSource[]; note?: string; error?: string };
  incomingReject: { available: boolean; reason: string; alternative?: string };
  switch?: string;
  row?: { id: number; cliNumber: string; operatorId: string; serviceId: string; businessId: string };
  values?: SettingsValues;
  transcribeNames?: string[];
  languages?: string[];
  languageNote?: string;
  switchError?: string;
}

/** A settings change. Absent = unchanged. webhookAuthToken absent keeps the current token. */
export interface SettingsChange {
  dialingCode?: string | null;
  transcribeName?: string | null;
  defaultLanguage?: string | null;
  webhookEnabled?: boolean | null;
  webhookUrl?: string | null;
  webhookAuthToken?: string | null;
  webhookClear?: boolean | null;
}

export interface SettingsPreview {
  mode: SwitchPushMode;
  previewHash: string;
  switchRowId: number;
  switchCliNumber: string;
  before: SettingsValues;
  after: SettingsValues;
  changedColumns: string[];
  warnings: string[];
}

const call = async <T>(fn: () => Promise<any>, fallback: string): Promise<APIResponse<T>> => {
  try { return responseFrom<T>((await fn()).data); }
  catch (e: any) { return errorFrom<T>(e, fallback); }
};

export const numberLifecycleService = {
  /** 200 = the caller may manage the lifecycle (master/platform or GG root admin); 403 = provider view. */
  mode: () => call<{ mode: SwitchPushMode }>(() => api.get(API_ENDPOINTS.NUMBER_LIFECYCLE_MODE), 'Could not read the lifecycle mode'),

  add: (providerId: number, numbers: string[], dryRun: boolean) =>
    call<AddResult>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_ADD, { providerId, numbers, dryRun }), 'Could not add the numbers'),

  flows: (providerId: number) =>
    call<FlowInfo[]>(() => api.get(API_ENDPOINTS.NUMBER_LIFECYCLE_FLOWS, { params: { providerId } }), 'Could not read the flows on this switch'),

  details: (numberId: number) =>
    call<NumberDetails>(() => api.get(API_ENDPOINTS.NUMBER_LIFECYCLE(numberId)), 'Could not load the number'),

  allotPreview: (numberId: number, req: AllotRequest) =>
    call<AllotPreview>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_ALLOT_PREVIEW(numberId), req), 'Could not preview the allotment'),

  allot: (numberId: number, req: AllotRequest) =>
    call<AllotResult>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_ALLOT(numberId), req), 'Could not allot the number'),

  pushPreview: (numberId: number, req: PushRequest) =>
    call<SwitchPushPreview>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_PUSH_PREVIEW(numberId), req), 'Could not preview the push'),

  pushApply: (numberId: number, req: PushRequest) =>
    call<SwitchPushApplyResult>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_PUSH_APPLY(numberId), req), 'Could not apply the push'),

  release: (numberId: number, reason: string, force: boolean, park: boolean) =>
    call<ReleaseResult>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_RELEASE(numberId), { reason, force, park }), 'Could not release the number'),

  retire: (numberId: number, reason: string, park: boolean) =>
    call<{ number: ProviderNumber; switch?: unknown }>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_RETIRE(numberId), { reason, park }), 'Could not retire the number'),

  /** holderOrganizationId: the would-be holder during an allot preview (default: the current holder). */
  addBillingPattern: (numberId: number, organizationId?: string | null, label?: string | null, holderOrganizationId?: string | null) =>
    call<PatternResult>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_PATTERN(numberId), { organizationId, label, holderOrganizationId }),
      'Could not add the billing pattern'),

  rollback: (pushId: number) =>
    call<SwitchPushRecord>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_ROLLBACK(pushId)), 'Could not roll back this push'),

  settings: (numberId: number) =>
    call<NumberSettings>(() => api.get(API_ENDPOINTS.NUMBER_LIFECYCLE_SETTINGS(numberId)), 'Could not load the settings'),

  settingsPreview: (numberId: number, change: SettingsChange) =>
    call<SettingsPreview>(() => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_SETTINGS_PREVIEW(numberId), change), 'Could not preview the change'),

  settingsApply: (numberId: number, change: SettingsChange, previewHash: string) =>
    call<{ pushId: number; status: string; detail: string; cacheRefreshed?: boolean | null }>(
      () => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_SETTINGS_APPLY(numberId), { change, previewHash }), 'Could not apply the change'),

  setOutbound: (numberId: number, allowed: boolean, reason?: string) =>
    call<{ outboundAllowed: boolean; changed: boolean }>(
      () => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_OUTBOUND(numberId), { allowed, reason }), 'Could not change outbound'),

  setCallerLookup: (numberId: number, enabled: boolean) =>
    call<{ callerLookup: boolean; changed: boolean; sources: CallerLookupSource[] }>(
      () => api.post(API_ENDPOINTS.NUMBER_LIFECYCLE_CALLER_LOOKUP(numberId), { enabled }), 'Could not change caller lookup'),
};

export default numberLifecycleService;
